import type { ReactNode } from 'react';

import AcBadge, { type AcBadgeTone } from './AcBadge';

/**
 * ENTERPRISE-PATIENT-360-EXPERIENCE-V1 (V7-P4 slice): the shared HIGHLIGHTS
 * PANEL primitive -- the enterprise record-page "highlights" band that surfaces
 * the most decision-relevant facts at a glance (the 360 law's overview; #278 /
 * FND-17: the flat all-cards-equal hierarchy gives way to a scan-first set).
 *
 * Design-language idiom: a labeled band of STATUS FACTS -- each an uppercase
 * micro-label over a typed value chip (FIELDS-NOT-SENTENCES), on soft
 * elevation, with a money/next emphasis tier so a few facts read FIRST. Reuses
 * the shared AcBadge tones (never color-only: the value word carries meaning)
 * and the --ac-* token layer; NOT a second component family.
 *
 * PRESENTATION-ONLY: facts arrive fully resolved and typed from the caller;
 * this component fetches nothing, derives nothing, mutates nothing.
 */

export interface HighlightFact {
  /** Stable key (React key). */
  readonly key: string;
  /** Uppercase micro-label. */
  readonly label: string;
  /** The typed value node (rendered inside a tone chip when `tone` is set). */
  readonly value: ReactNode;
  /** Semantic status tone; omit for a plain (untoned) value. */
  readonly tone?: AcBadgeTone;
  /** Emphasis tier: money / current-decision facts read first. */
  readonly emphasis?: boolean;
  /** data-testid stamped on the fact cell. */
  readonly testId?: string;
}

export interface HighlightsPanelProps {
  readonly facts: readonly HighlightFact[];
  /** Accessible group label (semantic-label law). */
  readonly label?: string;
  readonly testId?: string;
}

export default function HighlightsPanel({
  facts,
  label = 'Highlights',
  testId,
}: HighlightsPanelProps): JSX.Element | null {
  if (facts.length === 0) return null;
  return (
    <section className="ac-highlights" aria-label={label} data-testid={testId}>
      {facts.map((fact) => (
        <div
          key={fact.key}
          className={
            fact.emphasis
              ? 'ac-highlights__item ac-highlights__item--emphasis'
              : 'ac-highlights__item'
          }
          data-testid={fact.testId}
        >
          <span className="ac-highlights__label">{fact.label}</span>
          {fact.tone !== undefined ? (
            <AcBadge tone={fact.tone}>{fact.value}</AcBadge>
          ) : (
            <span className="ac-highlights__value">{fact.value}</span>
          )}
        </div>
      ))}
    </section>
  );
}
